import { Box, Button, Card, Typography } from '@mui/material';
import styled, { css } from 'styled-components';
import { color, duration, ease, font, radius, shadow } from '../../../theme/tokens';
import { floaty, kenBurns, pulseRing, reducedMotion } from '../../atome/motion/motion.styled';

export const ProductSection = styled.section<{ $solo?: boolean }>`
  position: relative;
  padding: ${({ $solo }) => ($solo ? '4.5rem 0 3rem' : '3rem 0 2.5rem')};
  background: ${({ $solo }) =>
    $solo
      ? `radial-gradient(120% 90% at 12% 0%, ${color.sandLight} 0%, ${color.cream} 55%, ${color.cream} 100%)`
      : color.cream};
  overflow: hidden;
`;

/** Soft out-of-focus orbs behind the spotlight product. */
export const Orb = styled.div<{ $size: string; $top: string; $left: string; $delay?: string }>`
  position: absolute;
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left};
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(226, 213, 196, 0.85), rgba(226, 213, 196, 0));
  filter: blur(6px);
  pointer-events: none;
  animation: ${floaty} 9s ${ease.inOut} infinite;
  animation-delay: ${({ $delay }) => $delay || '0s'};
  ${reducedMotion};
`;

export const SoloRibbon = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.5rem 1.1rem;
  border-radius: ${radius.pill};
  background: ${color.forest};
  color: #fff;
  font-family: ${font.body};
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  box-shadow: 0 10px 24px rgba(74, 112, 97, 0.28);
`;

export const SoloHeading = styled(Typography)`
  && {
    font-family: ${font.display};
    font-size: clamp(2.4rem, 5vw, 3.9rem);
    line-height: 1.05;
    font-weight: 600;
    color: ${color.ink};
    margin: 1rem 0 0.6rem;
  }
`;

export const SoloSubheading = styled(Typography)`
  && {
    font-family: ${font.body};
    color: ${color.inkSoft};
    max-width: 44ch;
    margin: 0 auto;
    font-size: 1.02rem;
    line-height: 1.7;
  }
`;

/* ----------------------------- gallery ----------------------------- */

export const Gallery = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

export const Frame = styled.div<{ $solo?: boolean }>`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  border-radius: ${radius.lg};
  overflow: hidden;
  background: linear-gradient(150deg, ${color.sandLight} 0%, ${color.paper} 100%);
  box-shadow: ${({ $solo }) => ($solo ? shadow.glow : shadow.soft)};

  /* thin inner hairline for the editorial "framed print" feel */
  &::after {
    content: '';
    position: absolute;
    inset: 10px;
    border: 1px solid rgba(255, 255, 255, 0.55);
    border-radius: calc(${radius.lg} - 8px);
    pointer-events: none;
  }

  @media (max-width: 900px) {
    aspect-ratio: 1 / 1;
  }
`;

export const Slide = styled.img<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transform: ${({ $active }) => ($active ? 'scale(1)' : 'scale(1.05)')};
  transition:
    opacity ${duration.slow} ${ease.out},
    transform 1.4s ${ease.out};
  ${({ $active }) =>
    $active &&
    css`
      animation: ${kenBurns} 12s ${ease.inOut} alternate infinite;
    `};
  ${reducedMotion};
`;

export const GalleryNav = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === 'left' ? 'left: 14px;' : 'right: 14px;')}
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border: none;
  cursor: pointer;
  border-radius: 50%;
  color: ${color.forest};
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(6px);
  box-shadow: 0 8px 20px rgba(43, 42, 39, 0.14);
  opacity: 0;
  transition:
    opacity ${duration.base} ${ease.out},
    transform ${duration.base} ${ease.spring},
    background ${duration.base} ${ease.out};
  z-index: 3;

  ${Frame}:hover & {
    opacity: 1;
  }

  &:hover {
    background: #fff;
    transform: translateY(-50%) scale(1.08);
  }

  /* touch devices never hover — keep the arrows visible there */
  @media (hover: none) {
    opacity: 1;
  }

  ${reducedMotion};
`;

export const Thumbs = styled.div`
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  /* A swipe past the last thumbnail must not chain out and pan the page. */
  overscroll-behavior-x: contain;
  padding-bottom: 4px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Thumb = styled.button<{ $active: boolean }>`
  flex: 0 0 auto;
  width: 74px;
  height: 74px;
  padding: 0;
  border-radius: ${radius.sm};
  overflow: hidden;
  cursor: pointer;
  background: ${color.paper};
  border: 1px solid ${({ $active }) => ($active ? color.forest : 'transparent')};
  outline: ${({ $active }) => ($active ? `3px solid rgba(74, 112, 97, 0.14)` : 'none')};
  opacity: ${({ $active }) => ($active ? 1 : 0.62)};
  transition:
    opacity ${duration.base} ${ease.out},
    transform ${duration.base} ${ease.spring},
    border-color ${duration.base} ${ease.out};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &:hover {
    opacity: 1;
    transform: translateY(-3px);
  }

  ${reducedMotion};
`;

export const FloatingBadge = styled.div`
  position: absolute;
  top: 18px;
  left: 18px;
  z-index: 2;
  padding: 0.45rem 0.9rem;
  border-radius: ${radius.pill};
  background: ${color.forest};
  color: #fff;
  font-family: ${font.body};
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  animation: ${pulseRing} 2.8s ${ease.out} infinite;
  ${reducedMotion};
`;

export const StockPill = styled.div<{ $tone: 'ok' | 'low' | 'out' }>`
  position: absolute;
  bottom: 18px;
  left: 18px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.42rem 0.9rem;
  border-radius: ${radius.pill};
  font-family: ${font.body};
  font-size: 0.76rem;
  font-weight: 500;
  color: ${({ $tone }) => ($tone === 'out' ? color.danger : $tone === 'low' ? color.warning : color.forest)};
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(6px);
  box-shadow: 0 6px 18px rgba(43, 42, 39, 0.1);

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
  }
`;

/* ----------------------------- details ----------------------------- */

export const ProductCard = styled(Card)`
  && {
    padding: 2.2rem 2rem;
    border-radius: ${radius.lg};
    background: rgba(255, 255, 255, 0.86);
    backdrop-filter: blur(10px);
    box-shadow: ${shadow.soft};
    border: 1px solid rgba(255, 255, 255, 0.7);
  }

  @media (max-width: 600px) {
    && {
      padding: 1.6rem 1.25rem;
    }
  }
`;

export const CategoryTag = styled.span`
  font-family: ${font.body};
  font-size: 0.7rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: ${color.forest};
`;

export const ProductTitle = styled(Typography)`
  && {
    font-family: ${font.display};
    font-weight: 600;
    font-size: clamp(1.9rem, 3.4vw, 2.7rem);
    line-height: 1.12;
    color: ${color.ink};
    margin: 0.4rem 0 0.2rem;
  }
`;

export const ProductDescription = styled(Typography)`
  && {
    font-family: ${font.body};
    color: ${color.inkSoft};
    margin: 1.4rem 0 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
`;

export const LineWrapper = styled.div<{ $delay?: number }>`
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  line-height: 1.6;
  font-size: 0.95rem;

  svg {
    flex: 0 0 auto;
    margin-top: 3px;
    transition: transform ${duration.base} ${ease.spring};
  }

  &:hover svg {
    transform: scale(1.25) rotate(-8deg);
  }
`;

export const Divider = styled.div`
  height: 1px;
  margin: 1.6rem 0;
  background: linear-gradient(90deg, ${color.line}, transparent);
`;

export const Price = styled(Box)`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: 0.4rem 0 0.2rem;
`;

export const CurrentPrice = styled(Typography)`
  && {
    font-family: ${font.display};
    font-weight: 600;
    font-size: 2.2rem;
    line-height: 1;
    color: ${color.forest};
  }
`;

export const OriginalPrice = styled(Typography)`
  && {
    font-family: ${font.body};
    text-decoration: line-through;
    color: ${color.inkFaint};
    font-size: 1.05rem;
  }
`;

export const SaveChip = styled.span`
  padding: 0.3rem 0.7rem;
  border-radius: ${radius.pill};
  background: ${color.sand};
  color: ${color.forestDeep};
  font-family: ${font.body};
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.05em;
`;

export const PriceHint = styled(Typography)`
  && {
    font-family: ${font.body};
    color: ${color.inkFaint};
    font-size: 0.8rem;
    margin-top: 0.35rem;
  }
`;

export const QuantitySelector = styled(Box)`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 1.5rem 0 1.2rem;
`;

export const QtyButton = styled.button`
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  cursor: pointer;
  color: ${color.forest};
  background: transparent;
  border: 1px solid ${color.sandDeep};
  transition:
    background ${duration.fast} ${ease.out},
    color ${duration.fast} ${ease.out},
    transform ${duration.fast} ${ease.spring};

  &:hover:not(:disabled) {
    background: ${color.forest};
    border-color: ${color.forest};
    color: #fff;
    transform: scale(1.08);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  ${reducedMotion};
`;

export const QtyValue = styled.span`
  min-width: 3rem;
  text-align: center;
  font-family: ${font.body};
  font-size: 1.15rem;
  font-weight: 500;
  color: ${color.ink};
`;

export const CheckoutButton = styled(Button)`
  && {
    position: relative;
    overflow: hidden;
    background: ${color.forest};
    color: #fff;
    font-family: ${font.body};
    padding: 0.95rem 1.6rem;
    font-size: 1rem;
    font-weight: 500;
    letter-spacing: 0.04em;
    border-radius: ${radius.pill};
    box-shadow: 0 14px 30px rgba(74, 112, 97, 0.26);
    transition:
      transform ${duration.base} ${ease.spring},
      box-shadow ${duration.base} ${ease.out},
      background ${duration.base} ${ease.out};
  }

  /* sheen sweep on hover */
  &&::before {
    content: '';
    position: absolute;
    top: 0;
    left: -120%;
    width: 60%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.35), transparent);
    transition: left 0.7s ${ease.out};
  }

  &&:hover {
    background: ${color.forestDeep};
    transform: translateY(-3px);
    box-shadow: 0 20px 42px rgba(74, 112, 97, 0.34);
  }

  &&:hover::before {
    left: 130%;
  }

  &&:disabled {
    background: ${color.sandDeep};
    color: rgba(255, 255, 255, 0.9);
    box-shadow: none;
    transform: none;
  }

  ${reducedMotion};
`;

export const TrustRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.2rem;
  margin-top: 1.4rem;
  padding-top: 1.2rem;
  border-top: 1px solid ${color.lineSoft};

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    font-family: ${font.body};
    font-size: 0.8rem;
    color: ${color.inkSoft};
  }

  svg {
    color: ${color.forest};
    font-size: 1rem;
  }
`;

export const TapeCaption = styled.div`
  text-align: center;
  margin-bottom: 0.5rem;

  h3 {
    font-family: ${font.display};
    font-size: clamp(1.6rem, 3vw, 2.2rem);
    font-weight: 600;
    color: ${color.ink};
    margin: 0.5rem 0 0.3rem;
  }

  p {
    font-family: ${font.body};
    color: ${color.inkSoft};
    margin: 0;
    font-size: 0.95rem;
  }
`;
